import { useState, useEffect } from "react";
import { Upload, FileVideo, Check, AlertCircle, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECS = [
  "Formato: MP4 (codec H.264) o WebM",
  "Durata consigliata: max 30 secondi",
  "Dimensione massima: 50MB",
  "Risoluzione: 1080p (1920x1080) consigliata",
  "Bitrate: 2-5 Mbps per bilanciare qualita' e peso",
];

export default function AdminUpload() {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [error, setError] = useState("");
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      const res = await axios.get(`${API}/files`);
      setFiles(res.data);
    } catch {}
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setError("");
    setUploaded(null);

    const allowed = ["video/mp4", "video/webm", "video/quicktime", "image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Tipo di file non supportato. Usa MP4, WebM, JPEG, PNG o WebP.");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError("File troppo grande. Massimo 50MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(res.data);
      fetchFiles();
    } catch (err) {
      setError(err.response?.data?.detail || "Errore durante l'upload.");
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="min-h-screen bg-w-cream" data-testid="admin-upload">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/')}
          data-testid="admin-back"
          className="flex items-center gap-2 text-w-muted hover:text-w-charcoal font-body text-sm mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna al sito
        </button>

        <h1 className="font-heading text-3xl md:text-4xl text-w-charcoal">Carica Media</h1>
        <p className="font-body text-w-muted mt-2 text-sm">
          Carica il video trailer o le foto per il sito del matrimonio.
        </p>

        {/* Specs */}
        <div className="border border-w-sand bg-white/40 p-5 mt-6">
          <h3 className="font-heading text-base text-w-charcoal mb-2">Specifiche consigliate</h3>
          <ul className="space-y-1">
            {SPECS.map((spec, i) => (
              <li key={i} className="font-body text-xs text-w-muted flex items-start gap-2">
                <span className="text-w-sage mt-0.5">&#8226;</span>{spec}
              </li>
            ))}
          </ul>
        </div>

        {/* Upload Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`mt-6 border-2 border-dashed p-10 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-w-sage bg-w-sage/5' : 'border-w-sand hover:border-w-sage/50'
          }`}
          onClick={() => document.getElementById('file-input').click()}
          data-testid="upload-dropzone"
        >
          <input
            id="file-input"
            type="file"
            accept="video/mp4,video/webm,video/quicktime,image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files[0])}
          />
          {uploading ? (
            <Loader2 className="w-8 h-8 animate-spin text-w-sage mx-auto" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-w-sage mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-body text-sm text-w-charcoal">Trascina un file qui o clicca per selezionare</p>
              <p className="font-body text-xs text-w-muted mt-1">MP4, WebM, JPEG, PNG, WebP (max 50MB)</p>
            </>
          )}
        </div>

        {/* Status */}
        {error && (
          <div className="mt-4 flex items-center gap-2 text-red-600">
            <AlertCircle className="w-4 h-4" />
            <p className="font-body text-sm">{error}</p>
          </div>
        )}
        {uploaded && (
          <div className="mt-4 flex items-center gap-2 text-w-sage">
            <Check className="w-4 h-4" />
            <p className="font-body text-sm">"{uploaded.original_filename}" caricato con successo!</p>
          </div>
        )}

        {/* Uploaded Files List */}
        {files.length > 0 && (
          <div className="mt-8">
            <h3 className="font-heading text-lg text-w-charcoal mb-3">File caricati</h3>
            <div className="space-y-2">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 border border-w-sand bg-white/40 p-3">
                  <FileVideo className="w-4 h-4 text-w-sage shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-sm text-w-charcoal truncate">{f.original_filename}</p>
                    <p className="font-body text-xs text-w-muted">
                      {(f.size / 1024 / 1024).toFixed(1)}MB &middot; {f.content_type}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
